import { useEffect, useMemo, useState } from "react";
import { Trash2, Plus, Zap, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import "@/components/teamproj/TeamProjections.css";

const PRESETS = [
  { label: "Συντηρητικό", value: 15 },
  { label: "Κανονικό", value: 20 },
  { label: "Χαλαρό", value: 25 },
];

const PTS_PRESETS = [
  { label: "Κανένα", value: 0 },
  { label: "Μέτριο", value: 2 },
  { label: "Έντονο", value: 4 },
];

// Admin editor for garbage-time / blowout scoring: how many points to add to a game's total at a
// full blowout (scaled down for closer games). Global value + optional per-team overrides.
export function GarbagePointsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [globalVal, setGlobalVal] = useState(2);
  const [refVal, setRefVal] = useState(20);
  const [newTeam, setNewTeam] = useState("");
  const [teamVal, setTeamVal] = useState(2);

  const load = (d) => { setData(d); setGlobalVal(d.global); setRefVal(d.ref_margin ?? 20); };
  useEffect(() => { api.get("/garbage-points").then((r) => load(r.data)).catch((e) => toast.error(errorText(e))); }, []);

  const codeOf = useMemo(() => Object.fromEntries((data?.teams_list || []).map((t) => [t.id, t.code])), [data]);

  const saveGlobal = async (val) => {
    const value = Number(val ?? globalVal);
    setBusy(true);
    try {
      const r = await api.put("/garbage-points/global", { value });
      setGlobalVal(r.data.global);
      setData({ ...data, ...r.data });
      toast.success(`Γενικό garbage time: +${r.data.global} στα blowouts`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const saveRef = async (val) => {
    const value = Number(val ?? refVal);
    setBusy(true);
    try {
      const r = await api.put("/garbage-points/ref", { value });
      setRefVal(r.data.ref);
      setData({ ...data, ...r.data, ref_margin: r.data.ref });
      toast.success(`Όριο blowout: διαφορά ${r.data.ref}+ πόντων`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const addTeam = async () => {
    if (!newTeam) return toast.error("Διάλεξε ομάδα");
    setBusy(true);
    try {
      const r = await api.put("/garbage-points/team", { team_id: newTeam, value: Number(teamVal) });
      setData({ ...data, ...r.data });
      toast.success(`${codeOf[newTeam] || newTeam}: +${teamVal} στα blowouts`);
      setNewTeam("");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const removeTeam = async (teamId) => {
    setBusy(true);
    try {
      const r = await api.delete(`/garbage-points/team/${teamId}`);
      setData({ ...data, ...r.data });
      toast.success(`${codeOf[teamId] || teamId}: επαναφορά στο γενικό`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const resetAll = async () => {
    if (!window.confirm("Επαναφορά ΟΛΩΝ των ανά-ομάδα ρυθμίσεων garbage time στο γενικό;")) return;
    setBusy(true);
    try {
      const r = await api.delete("/garbage-points/teams");
      setData({ ...data, ...r.data });
      toast.success("Όλες οι ανά-ομάδα ρυθμίσεις επανήλθαν στο γενικό");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const fullReset = async () => {
    if (!window.confirm("Πλήρης επαναφορά garbage time στις αρχικές προεπιλογές (+2 / όριο 20) και καθαρισμός όλων των ομάδων;")) return;
    setBusy(true);
    try {
      const r = await api.post("/garbage-points/reset");
      load({ ...data, ...r.data, ref_margin: r.data.ref });
      toast.success("Πλήρης επαναφορά: +2 στα blowouts, όριο 20, χωρίς ανά-ομάδα ρυθμίσεις");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const entries = Object.entries(data.teams || {});
  const available = (data.teams_list || []).filter((t) => !(t.id in (data.teams || {})));
  const ref = data.ref_margin ?? 20;

  return (
    <div className="tp-root mt-6">
      <div className="tp-card" data-testid="garbage-points-card">
        <h4><Zap size={13} className="inline mr-1" />ΠΟΝΤΟΙ GARBAGE TIME (BLOWOUT) <small>(admin · έξτρα πόντοι στο σύνολο ματς σε μεγάλη διαφορά)</small>
          <button type="button" className="tv-vbtn" onClick={fullReset} disabled={busy} data-testid="garbage-full-reset" style={{ float: "right", marginTop: -2 }}>
            <RotateCcw size={13} /> Πλήρης επαναφορά
          </button>
        </h4>
        <p className="tp-note" style={{ marginBottom: 6 }}>
          Προστίθενται στο σύνολο του αγώνα, κλιμακωτά με την προβλεπόμενη διαφορά: 0 σε ισόπαλο, πλήρως σε διαφορά {ref}+ πόντων (μοιράζονται στις δύο ομάδες). Η ανά-ομάδα τιμή υπερισχύει του γενικού.
        </p>

        <div className="tv-vform tv-margins" style={{ alignItems: "end", marginBottom: 4 }}>
          <label><small>Γενικό (όλες οι ομάδες)</small>
            <input type="number" step="0.5" min={data.hard?.min ?? 0} max={data.hard?.max ?? 15} value={globalVal}
              onChange={(e) => setGlobalVal(e.target.value)} data-testid="garbage-global-input" /></label>
          <button type="button" className="tv-vbtn primary" onClick={() => saveGlobal()} disabled={busy} data-testid="garbage-global-save">Αποθήκευση</button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }} data-testid="garbage-global-presets">
          {PTS_PRESETS.map((p) => (
            <button key={p.value} type="button" className={`tv-vbtn${Number(globalVal) === p.value ? " primary" : ""}`} disabled={busy}
              onClick={() => saveGlobal(p.value)} data-testid={`garbage-global-preset-${p.value}`}>
              {p.label} (+{p.value})
            </button>
          ))}
        </div>

        <div className="tv-vform tv-margins" style={{ alignItems: "end", marginBottom: 4 }}>
          <label><small>Όριο διαφοράς blowout (πλήρες garbage)</small>
            <input type="number" step="1" min={data.ref_hard?.min ?? 8} max={data.ref_hard?.max ?? 40} value={refVal}
              onChange={(e) => setRefVal(e.target.value)} data-testid="garbage-ref-input" /></label>
          <button type="button" className="tv-vbtn primary" onClick={() => saveRef()} disabled={busy} data-testid="garbage-ref-save">Αποθήκευση</button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }} data-testid="garbage-ref-presets">
          {PRESETS.map((p) => (
            <button key={p.value} type="button" className={`tv-vbtn${Number(refVal) === p.value ? " primary" : ""}`} disabled={busy}
              onClick={() => saveRef(p.value)} data-testid={`garbage-ref-preset-${p.value}`}>
              {p.label} ({p.value})
            </button>
          ))}
        </div>

        {entries.length === 0 ? <p className="tp-note" data-testid="garbage-teams-empty">Καμία ειδική ρύθμιση ανά ομάδα — όλες στο γενικό (+{data.global}).</p> : (
          <div data-testid="garbage-teams-list">
            {entries.map(([tid, v]) => (
              <div key={tid} className="tv-vform" data-testid={`garbage-team-${tid}`} style={{ alignItems: "center" }}>
                <span style={{ fontWeight: 700, minWidth: 48 }}>{codeOf[tid] || tid}</span>
                <span className="tp-note">+{v} στα blowouts</span>
                <button type="button" className="tv-vbtn" onClick={() => removeTeam(tid)} disabled={busy} data-testid={`garbage-team-remove-${tid}`}><Trash2 size={13} /> Επαναφορά</button>
              </div>
            ))}
            <button type="button" className="tv-vbtn" onClick={resetAll} disabled={busy} data-testid="garbage-reset-all" style={{ marginTop: 6 }}>
              <Trash2 size={13} /> Επαναφορά όλων στο γενικό
            </button>
          </div>
        )}

        <div className="tv-vform tv-margins" style={{ alignItems: "end", marginTop: 8 }}>
          <label><small>Ομάδα</small>
            <select value={newTeam} onChange={(e) => setNewTeam(e.target.value)} data-testid="garbage-team-select">
              <option value="">— Διάλεξε ομάδα —</option>
              {available.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
            </select></label>
          <label><small>Πόντοι</small>
            <input type="number" step="0.5" min={data.hard?.min ?? 0} max={data.hard?.max ?? 15} value={teamVal}
              onChange={(e) => setTeamVal(e.target.value)} data-testid="garbage-team-input" /></label>
          <button type="button" className="tv-vbtn primary" onClick={addTeam} disabled={busy} data-testid="garbage-team-add"><Plus size={14} /> Προσθήκη</button>
        </div>
      </div>
    </div>
  );
}
